import React, { FC, useMemo } from 'react';

import { Button, Icon, Text, VStack } from '@rbilabs/universal-components';
import { spacing } from '@rbilabs/universal-components/themes';
import { isString } from 'lodash-es';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { A11yWrapper } from 'components/a11y-wrapper';
import BlockRenderer from 'components/sanity-block-renderer';
import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useCart } from 'hooks/use-cart';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { getEvaluationResultMessage } from 'state/loyalty/hooks/utils/helpers';
import { isAnyWalmartOffer } from 'state/loyalty/hooks/utils/walmart';
import { LoyaltyOffer } from 'state/loyalty/types';
import { primitive } from 'styles/constants/primitives';

import { WalmartBadgeContainer } from './cart-item-offer';

const Content = VStack.withConfig({ flex: 1 });

const StyledBlockRenderer = BlockRenderer.withConfig({
  textProps: {
    fontWeight: 'bold',
    fontSize: 15,
    lineHeight: 'sm',
  },
});

const RemoveButton = Button.withConfig({
  borderRadius: 0,
  backgroundColor: 'transparent',
  _text: {
    fontSize: 'xs',
    color: primitive.bk.$bbqBrown,
  },
  _disabled: {
    color: Styles.color.grey.four,
  },
  _hover: {
    background: 'transparent',
  },
  _pressed: {
    background: 'transparent',
  },
});

export const CartItemOfferDiscount: FC<{
  selectedLoyaltyOffer: LoyaltyOffer;
}> = ({ selectedLoyaltyOffer }) => {
  const dispatch = useAppDispatch();
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();
  const { cartEntries, calculateCartTotalWithDiscount, removeFromCart } = useCart();
  const isWalmartOfferSelected = isAnyWalmartOffer(selectedLoyaltyOffer);
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  const formatters = useIntl();
  const { formatMessage } = formatters;

  const onRemove = () => {
    dispatch(actions.loyalty.setSelectedOffer(null));
    dispatch(actions.loyalty.setOffersFeedbackMap({}));
    dispatch(actions.loyalty.resetAppliedDiscountOffers());
    if (isUnifiedMenuEnabled) {
      removeFromCart({ cartId: DISCOUNT_OFFER_CART_ID });
    }
  };

  const { isValid = true, textOfferRequirementsNotMet = '' } = useMemo(() => {
    const { formatMessage } = formatters;
    const available = checkLoyaltyOfferAvailability(selectedLoyaltyOffer);
    const ruleErrors = offersFeedbackMap[selectedLoyaltyOffer.loyaltyEngineId || ''];
    const hasRuleErrors = Boolean(ruleErrors?.length);
    const { isCartTotalNegative = false } = calculateCartTotalWithDiscount();

    let errorMessage: React.ReactNode | undefined;
    if (!available) {
      errorMessage = formatMessage({ id: 'offerNotAvailableInRestaurantPos' });
    } else if (isCartTotalNegative) {
      errorMessage = formatMessage({ id: 'cartOfferRequirementsNotMetError' });
    } else if (hasRuleErrors) {
      // Show first error only
      errorMessage = getEvaluationResultMessage({
        evaluationResult: ruleErrors[0],
        formatters,
        incentiveType: LoyaltyPromotionType.OFFER,
        cartEntries,
        forceOnlyStringMessage: true,
      });
    }

    return {
      isValid: available && !hasRuleErrors && !isCartTotalNegative,
      textOfferRequirementsNotMet: errorMessage,
    };
  }, [
    checkLoyaltyOfferAvailability,
    formatters,
    offersFeedbackMap,
    calculateCartTotalWithDiscount,
    selectedLoyaltyOffer,
    cartEntries,
  ]);

  const iconVariant = textOfferRequirementsNotMet ? 'errorV2' : 'checkFill';
  const offerMessage = formatMessage({
    id: isValid ? 'offerApplied' : 'offerNotApplied',
  });

  useLogUserErrorMessage({
    message: offerMessage,
    logWhenTrue: !isValid,
    severity: LogUserErrorSeverity.Recoverable,
    context: {
      selectedOfferName: selectedLoyaltyOffer?.name,
      textOfferRequirementsNotMet: isString(textOfferRequirementsNotMet)
        ? textOfferRequirementsNotMet
        : null,
    },
  });

  const offerStatusText = (
    <A11yWrapper>
      <Text focusable={true} fontSize="2xs" lineHeight="sm">
        {offerMessage}
        {textOfferRequirementsNotMet && ` - ${textOfferRequirementsNotMet}`}
      </Text>
    </A11yWrapper>
  );

  const offerName = <StyledBlockRenderer content={selectedLoyaltyOffer?.name?.localeRaw} />;

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: textOfferRequirementsNotMet
            ? primitive.bk.$errorLight
            : primitive.bk.$successLight,
        },
      ]}
    >
      <Icon alignSelf="center" marginRight="$2" marginLeft={'$2'} variant={iconVariant} size="4" />
      <Content>
        {isWalmartOfferSelected && <WalmartBadgeContainer marginBottom="$0.5" />}
        {offerName}
        {offerStatusText}
      </Content>
      <RemoveButton
        accessibilityLabel={`${formatMessage({ id: 'remove' })} ${selectedLoyaltyOffer.name} `}
        onPress={onRemove}
      >
        {formatMessage({ id: 'remove' })}
      </RemoveButton>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    flexDirection: 'row',
    paddingVertical: spacing.$2,
    paddingHorizontal: spacing.$1,
    marginBottom: spacing.$3,
    borderRadius: 8,
  },
});
